import { AccommodationIcon, BalanceIcon, CalendarIcon, FileIcon, FlightIcon, GroupsIcon, IdeaIcon, SettingsIcon } from '@/assets/icons'
import { useBreadcrumbs } from '@context/BreadcrumbContext'
import { convertDateRangeToPayload, useEvents, useFiles, useFlights, useIdeas, useMe, usePaymentsOwed, useTrip, useTripMembers, useUpdateTripDates } from '@hooks'
import { useAccommodations } from '@hooks/useAccommodations'
import { computeNetBalanceForUser, formatBalanceTileMetric, summarizeBalanceForBanner } from '@/utils/balanceCalculations'
import { TripHelper } from '@lib/TripHelper'
import { isConflict } from '@lib/apiError'
import React, { useState } from 'react'
import toast from 'react-hot-toast'
import { useNavigate, useParams } from 'react-router-dom'
import PageLoader from '@components/PageLoader'
import EditTripDatesModal from '../../Itinerary/EditTripDatesModal/EditTripDatesModal'
import EditTripModal from '../EditTripModal'
import {
    DashboardCard,
    DashboardCardContent,
    DashboardCardDescription,
    DashboardCardFooter,
    DashboardCardHeader,
    DashboardCardIcon,
    DashboardCardMetric,
    DashboardCardMetrics,
    DashboardCardTitle,
    DashboardContainer,
    DashboardGrid,
    DashboardHeader,
    DashboardTitle,
    DashboardTitleSection,
    DashboardWrapper,
    DateInfoContainer,
    EmptyStateMessage,
    ErrorState,
    NavigateButton,
    SettingsButton,
    SettingsButtonContainer,
    TripCode,
    TripCodeContainer,
    TripCodeLabel
} from './TripDetailPage.styles'

const TripDetailPage: React.FC = () => {
	const { tripId } = useParams<{ tripId: string }>()
	const navigate = useNavigate()
	const { data: trip, isLoading: tripLoading } = useTrip(tripId)
	const { data: me } = useMe()
	const { data: eventsData } = useEvents(tripId)
	const { data: ideasData } = useIdeas(tripId)
	const { data: membersData } = useTripMembers(tripId)
	const { data: accommodationsData } = useAccommodations(tripId)
	const { data: flightsData } = useFlights(tripId)
	const { data: filesData } = useFiles(tripId)
	const { data: paymentsOwedData } = usePaymentsOwed(tripId)
	const [isEditModalOpen, setIsEditModalOpen] = useState(false)
	const [isEditTripModalOpen, setIsEditTripModalOpen] = useState(false)
	const updateTripDates = useUpdateTripDates(tripId || '')

	// Set breadcrumbs - must be called before any early returns
	useBreadcrumbs(
		trip
			? [
					{ label: 'My Trips', path: '/trips' },
					{ label: trip.name },
				]
			: []
	)

	if (tripLoading) {
		return (
			<DashboardWrapper>
				<PageLoader label="Loading trip details…" />
			</DashboardWrapper>
		)
	}

	if (!trip) {
		return (
			<DashboardWrapper>
				<DashboardContainer>
					<ErrorState>Trip not found</ErrorState>
				</DashboardContainer>
			</DashboardWrapper>
		)
	}

	const eventsCount = eventsData?.events?.length || 0
	const ideasCount = ideasData?.ideas?.length || 0
	const membersCount = membersData?.members?.length || 0
	const accommodationsCount = accommodationsData?.accommodations?.length || 0
	const flightsCount = flightsData?.flightGroups?.length || 0
	const filesCount = filesData?.files?.length || 0

	const balanceSummary = (() => {
		if (!me || !paymentsOwedData) {
			return { kind: 'settled' as const }
		}
		const netBalance = computeNetBalanceForUser(paymentsOwedData.paymentsOwed, me.id)
		return summarizeBalanceForBanner(netBalance)
	})()
	const balanceMetric = formatBalanceTileMetric(balanceSummary, trip?.defaultCurrencyCode ?? 'usd')

	const tripHelper = new TripHelper(trip)
	const hasTripDates = !!trip.startDate
	const startDate = trip.startDate ? TripHelper.formatDate(trip.startDate) : 'Not set'
	const endDate = trip.startDate && trip.dayCount > 0 ? tripHelper.getFormattedEndDate() : 'Not set'
	const daysCount = trip.dayCount || 0

	const tripStartDate = trip?.startDate ? TripHelper.convertTripDateToInputFormat(trip.startDate) : ''
	const tripEndDate = trip
		? (() => {
				if (!trip.startDate || !trip.dayCount) return ''
				const tripHelper = new TripHelper(trip)
				const endDate = tripHelper.getEndDate()
				return endDate ? endDate.toISOString().split('T')[0] : ''
			})()
		: ''

	const handleItineraryClick = () => {
		if (!hasTripDates) {
			setIsEditModalOpen(true)
		} else {
			navigate(`/trips/${tripId}/itinerary`)
		}
	}

	const handleSaveTripDates = (startDate: string, endDate: string) => {
		if (trip && tripId) {
			const payload = convertDateRangeToPayload({ startDate, endDate })
			updateTripDates.mutate(payload, {
				onSuccess: () => {
					toast.success('Trip dates updated successfully!')
					setIsEditModalOpen(false)
				},
				onError: (error) => {
					if (isConflict(error)) {
						toast.error(
							'Cannot shorten trip dates. You have events scheduled on days that would be removed. Please delete or move those events first.',
							{ duration: 10000 }
						)
					} else {
						toast.error('Failed to update trip dates. Please try again.')
					}
				},
			})
		}
	}

	const handleIdeasClick = () => {
		navigate(`/trips/${tripId}/ideas`)
	}

	const handleMembersClick = () => {
		navigate(`/trips/${tripId}/members`)
	}

	const handleAccommodationsClick = () => {
		navigate(`/trips/${tripId}/accommodations`)
	}

	const handleFlightsClick = () => {
		navigate(`/trips/${tripId}/flights`)
	}

	const handleFilesClick = () => {
		navigate(`/trips/${tripId}/files`)
	}

	const handleBalancesClick = () => {
		navigate(`/trips/${tripId}/balances`)
	}

	const handleTripSettingsClick = () => {
		setIsEditTripModalOpen(true)
	}

	return (
		<DashboardWrapper>
			<DashboardContainer>
				<DashboardHeader>
					<DashboardTitleSection>
						<DashboardTitle>{trip.name}</DashboardTitle>
						<TripCodeContainer>
							<TripCodeLabel>Trip Code:</TripCodeLabel>
							<TripCode>{trip.code}</TripCode>
						</TripCodeContainer>
					</DashboardTitleSection>
					<SettingsButtonContainer>
						<SettingsButton onClick={handleTripSettingsClick}>
							<SettingsIcon width={20} height={20} fill="currentColor" />
							<span>Trip Settings</span>
						</SettingsButton>
					</SettingsButtonContainer>
				</DashboardHeader>

				<DashboardGrid>
				{/* Itinerary Card */}
				<DashboardCard>
					<DashboardCardHeader>
						<DashboardCardIcon>
							<CalendarIcon width={24} height={24} fill="currentColor" />
						</DashboardCardIcon>
						<DashboardCardTitle>Itinerary</DashboardCardTitle>
					</DashboardCardHeader>
					<DashboardCardContent>
						{!hasTripDates ? (
							<EmptyStateMessage>
								Set trip dates to start planning your itinerary
							</EmptyStateMessage>
						) : (
							<>
								<DashboardCardMetrics>
									<DashboardCardMetric>
										<span className="metric-value">{eventsCount}</span>
										<span className="metric-label">Events</span>
									</DashboardCardMetric>
									<DashboardCardMetric>
										<span className="metric-value">{daysCount}</span>
										<span className="metric-label">Days</span>
									</DashboardCardMetric>
								</DashboardCardMetrics>
								<DateInfoContainer>
									<div>Start: {startDate}</div>
									<div>End: {endDate}</div>
								</DateInfoContainer>
							</>
						)}
					</DashboardCardContent>
					<DashboardCardFooter>
						<NavigateButton onClick={handleItineraryClick}>
							{!hasTripDates ? 'Set Trip Dates' : 'View Itinerary →'}
						</NavigateButton>
					</DashboardCardFooter>
				</DashboardCard>

				{/* Accommodations Card */}
				<DashboardCard>
					<DashboardCardHeader>
						<DashboardCardIcon>
							<AccommodationIcon width={24} height={24} fill="currentColor" />
						</DashboardCardIcon>
						<DashboardCardTitle>Accommodations</DashboardCardTitle>
					</DashboardCardHeader>
					<DashboardCardContent>
						<DashboardCardMetrics>
							<DashboardCardMetric>
								<span className="metric-value">{accommodationsCount}</span>
								<span className="metric-label">{accommodationsCount === 1 ? 'Place' : 'Places'}</span>
							</DashboardCardMetric>
						</DashboardCardMetrics>
					</DashboardCardContent>
					<DashboardCardDescription>
						Make sure everyone has a place to stay. Add hotels, Airbnbs, and other lodging.
					</DashboardCardDescription>
					<DashboardCardFooter>
						<NavigateButton onClick={handleAccommodationsClick}>
							View Accommodations →
						</NavigateButton>
					</DashboardCardFooter>
				</DashboardCard>

				{/* Flights Card */}
				<DashboardCard>
					<DashboardCardHeader>
						<DashboardCardIcon>
							<FlightIcon width={24} height={24} fill="currentColor" />
						</DashboardCardIcon>
						<DashboardCardTitle>Flights</DashboardCardTitle>
					</DashboardCardHeader>
					<DashboardCardContent>
						<DashboardCardMetrics>
							<DashboardCardMetric>
								<span className="metric-value">{flightsCount}</span>
								<span className="metric-label">{flightsCount === 1 ? 'Flight' : 'Flights'}</span>
							</DashboardCardMetric>
						</DashboardCardMetrics>
					</DashboardCardContent>
					<DashboardCardDescription>
						Track everyone's flights so the group knows who's arriving and departing when.
					</DashboardCardDescription>
					<DashboardCardFooter>
						<NavigateButton onClick={handleFlightsClick}>
							View Flights →
						</NavigateButton>
					</DashboardCardFooter>
				</DashboardCard>

				{/* Ideas Card */}
				<DashboardCard>
					<DashboardCardHeader>
						<DashboardCardIcon>
							<IdeaIcon width={24} height={24} fill="currentColor" />
						</DashboardCardIcon>
						<DashboardCardTitle>Ideas</DashboardCardTitle>
					</DashboardCardHeader>
					<DashboardCardContent>
						<DashboardCardMetrics>
							<DashboardCardMetric>
								<span className="metric-value">{ideasCount}</span>
								<span className="metric-label">Total</span>
							</DashboardCardMetric>
						</DashboardCardMetrics>
					</DashboardCardContent>
                    <DashboardCardDescription>
						Share and save potential trip ideas here. Such as social media links, locations and more that can later be added to your itinerary.
					</DashboardCardDescription>
					<DashboardCardFooter>
						<NavigateButton onClick={handleIdeasClick}>View Ideas →</NavigateButton>
					</DashboardCardFooter>
				</DashboardCard>

				{/* Members Card */}
				<DashboardCard>
					<DashboardCardHeader>
						<DashboardCardIcon>
							<GroupsIcon width={24} height={24} fill="currentColor" />
						</DashboardCardIcon>
						<DashboardCardTitle>Members</DashboardCardTitle>
					</DashboardCardHeader>
					<DashboardCardContent>
						<DashboardCardMetrics>
							<DashboardCardMetric>
								<span className="metric-value">{membersCount}</span>
								<span className="metric-label">Total</span>
							</DashboardCardMetric>
						</DashboardCardMetrics>
					</DashboardCardContent>
					<DashboardCardFooter>
						<NavigateButton onClick={handleMembersClick}>View Members →</NavigateButton>
					</DashboardCardFooter>
				</DashboardCard>

				{/* Files Card */}
				<DashboardCard>
					<DashboardCardHeader>
						<DashboardCardIcon>
							<FileIcon width={24} height={24} fill="currentColor" />
						</DashboardCardIcon>
						<DashboardCardTitle>Files</DashboardCardTitle>
					</DashboardCardHeader>
					<DashboardCardContent>
						<DashboardCardMetrics>
							<DashboardCardMetric>
								<span className="metric-value">{filesCount}</span>
								<span className="metric-label">{filesCount === 1 ? 'File' : 'Files'}</span>
							</DashboardCardMetric>
						</DashboardCardMetrics>
					</DashboardCardContent>
					<DashboardCardDescription>
						Upload and share photos, documents, and other files with your trip group.
					</DashboardCardDescription>
					<DashboardCardFooter>
						<NavigateButton onClick={handleFilesClick}>View Files →</NavigateButton>
					</DashboardCardFooter>
				</DashboardCard>

				{/* Balances Card */}
				<DashboardCard>
					<DashboardCardHeader>
						<DashboardCardIcon>
							<BalanceIcon width={24} height={24} fill="currentColor" />
						</DashboardCardIcon>
						<DashboardCardTitle>Balances</DashboardCardTitle>
					</DashboardCardHeader>
					<DashboardCardContent>
						<DashboardCardMetrics>
							<DashboardCardMetric>
								<span className="metric-value">{balanceMetric.value}</span>
								<span className="metric-label">{balanceMetric.label}</span>
							</DashboardCardMetric>
						</DashboardCardMetrics>
					</DashboardCardContent>
					<DashboardCardDescription>
						Track expenses and payups, see who owes whom, and settle up in one place.
					</DashboardCardDescription>
					<DashboardCardFooter>
						<NavigateButton onClick={handleBalancesClick}>View Balances →</NavigateButton>
					</DashboardCardFooter>
				</DashboardCard>
				</DashboardGrid>
			</DashboardContainer>

		<EditTripDatesModal
			isOpen={isEditModalOpen}
			onClose={() => setIsEditModalOpen(false)}
			onSave={handleSaveTripDates}
			initialStartDate={tripStartDate}
			initialEndDate={tripEndDate}
			isUpdating={updateTripDates.isPending}
		/>

		<EditTripModal
			isOpen={isEditTripModalOpen}
			onClose={() => setIsEditTripModalOpen(false)}
			tripId={tripId || ''}
			initialName={trip.name}
			initialCurrencyCode={trip.defaultCurrencyCode}
			initialIsVisibleToPublic={trip.isVisibleToPublic}
			isOwner={me ? me.id === trip.ownerUserId : undefined}
		/>
	</DashboardWrapper>
)
}

export default TripDetailPage

